import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import { Course, CourseTemplate, AssessmentCategory, Assessment, ALP } from '../../types';
import { useFirestoreCollection } from '../../hooks/useFirestoreCollection';
import { AssessmentContext } from './AssessmentContext';

interface AppDataContextType {
    courses: Course[];
    courseTemplates: CourseTemplate[];

    // Global Templates (Used during course creation)
    templateAssessmentCategories: AssessmentCategory[];
    templateAssessments: Assessment[];

    // Alps
    alps: ALP[];
}

const AppDataContext = createContext<AppDataContextType | undefined>(undefined);

const DEFAULT_EMPTY_ARRAY: any[] = [];

export const AppDataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    // Phase 1: Core Data
    const [courses] = useFirestoreCollection<Course>('courses', DEFAULT_EMPTY_ARRAY);
    const [courseTemplates] = useFirestoreCollection<CourseTemplate>('courseTemplates', DEFAULT_EMPTY_ARRAY);

    // Global Templates State
    const [templateAssessmentCategories] = useFirestoreCollection<AssessmentCategory>('templateAssessmentCategories', DEFAULT_EMPTY_ARRAY);
    const [templateAssessments] = useFirestoreCollection<Assessment>('templateAssessments', DEFAULT_EMPTY_ARRAY);

    // ALP State
    const [alps] = useFirestoreCollection<ALP>('alps', DEFAULT_EMPTY_ARRAY);

    const value = useMemo(() => ({
        courses,
        courseTemplates,
        templateAssessmentCategories,
        templateAssessments,
        alps
    }), [courses, courseTemplates, templateAssessmentCategories, templateAssessments, alps]);

    return (
        <AppDataContext.Provider value={value}>
            {children}
        </AppDataContext.Provider>
    );
};

export const useAppData = () => {
    const context = useContext(AppDataContext);
    if (!context) {
        throw new Error('useAppData must be used within an AppDataProvider');
    }
    return context;
};
